<template>
  <div class="auth-page loading">
    <spinner-loader :loadingStuff="loadingStuff" />
    <main>
      <b-alert v-if="deferredPrompt !== null" variant="success" show class="installAlert">
        <p>
          Install the app onto your device so you can receive notifications.
        </p>
        <div class="button-wrappers">
          <button @click="install">Install</button>
          <button @click="dismiss">Dismiss</button>
        </div>
      </b-alert>
      <div class="content content--side">
        <header class="codrops-header uk-flex uk-flex-center">
          <TenantLogo logo-class="login-logo" />
          <h1 class="uk-margin-remove uk-text-center login-header">
            {{ $store.getters.clubNameShort }}
            <span>{{ $store.getters.appName }}</span>
          </h1>
        </header>
        <div v-if="accessBlock" class="access-block">
          <h2><i class="bi bi-lock"></i> One more step</h2>
          <p>
            <strong>{{ accessBlock.playerName }}</strong>'s place is confirmed once the
            <strong>{{ accessBlock.amountLabel }}</strong> deposit is paid. The app opens
            as soon as the club has it.
          </p>
          <a :href="accessBlock.payLink" class="pay-button">Pay the deposit by card</a>
          <p class="muted">
            Already paid by bank transfer? The club marks it as received on their side —
            get in touch with them if it's been a few days.
          </p>
          <button type="button" class="link-button" @click="accessBlock = null">Back to login</button>
        </div>
        <form v-else class="form" v-on:submit.prevent="login()">
          <p v-if="validation" class="uk-text-danger">
            {{ validationText }}
          </p>
          <div class="form__item">
            <label class="form__label" for="email">Email Address</label>
            <input class="form__input" type="email" v-model="email" id="email" />
          </div>
          <div class="form__item">
            <label class="form__label" for="password">Password</label>
            <div class="form__input-wrap">
              <input class="form__input" type="password" v-model="password" id="password" />
              <p class="form__password-strength" id="strength-output"></p>
              <span class="reset-password">
                <a @click="openResetPasswordModal()" href="javascript:;">Reset Password</a>
              </span>
            </div>
          </div>
          <div class="form__item form__item--actions">
            <button type="submit">Log in</button>
          </div>
        </form>
        <p class="install-help">
          New here or setting up your phone?
          <a href="/training-course.html#lesson-0">How to install &amp; register the app</a>
        </p>
      </div>
    </main>

    <b-modal v-model="showResetPasswordModal" size="lg" title="Reset password" :hide-footer="true">
      <div v-if="emailsentValue === false" class="form">
        <p>
          Enter your email and we will send you a link to reset your password.
        </p>
        <div class="form__item">
          <label class="form__label" for="reset-email">Email Address</label>
          <input class="form__input" type="email" v-model="email" id="reset-email" />
        </div>

        <button @click="emailResetPasswordLink()" class="green-button">
          Send link to email
        </button>
      </div>
      <div v-else class="form">
        <p>
          Email sent, check your email and open the link we sent you to
          continue.
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import img from "./img/login.jpg";
import axios from "axios";
import { AUTH_REQUEST } from "@/store/actions/auth";
import { USER_POPULATE } from "@/store/actions/user";
import { Users } from "@/services/user-service.js";
import { PasswordReset } from "@/services/password-reset-service.js";
import { getClubId } from "@/config/tenant";
import { Storage } from "@/services/storage";
import SpinnerLoader from "../../../components/LoadingSpinning";
import TenantLogo from "@/components/TenantLogo/index.vue";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
export default {
  name: "Login",
  components: {
    SpinnerLoader,
    TenantLogo,
  },
  data() {
    return {
      loadingStuff: false,
      deferredPrompt: null,
      emailsentValue: false,
      showResetPasswordModal: false,
      email: "",
      password: "",
      action: "login",
      img: img,
      validation: false,
      validationText: "Invalid Username or Password!",
      // Set when the backend refuses the login until a trial deposit is paid.
      accessBlock: null,
    };
  },
  mounted() {
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      // Stash the event so it can be triggered later.
      this.deferredPrompt = e;
    });
    window.addEventListener("appinstalled", () => {
      this.deferredPrompt = null;
    });
  },
  methods: {
    async dismiss() {
      this.deferredPrompt = null;
    },
    async install() {
      this.deferredPrompt.prompt();
    },
    async emailResetPasswordLink() {
      if (!this.email || this.email.trim() === '') {
        this.$snack.danger({ text: "Please enter your email address" });
        return;
      }

      try {
        const email = this.email.trim();

        // Handled server-side now. This used to call Firebase from the browser,
        // which meant working around the fact that Firebase accounts are created
        // lazily on first login: a member who had never signed in had no Firebase
        // user, so Firebase accepted the request and silently sent nothing. The
        // workaround was to create their account here with a throwaway password —
        // which also signed that account in and had to be signed out again.
        //
        // The backend now creates the missing account with the Admin SDK and
        // sends the link through the club's own Brevo sender, so the email is
        // tenant-branded and its delivery is visible. Same endpoint serves the
        // mobile app, so both routes behave identically.
        // Nobody is signed in on this screen, so the club comes from the
        // build's baked tenant config, not from user data.
        await PasswordReset.request(getClubId(), email);

        this.emailsentValue = true;
        this.$snack.success({ text: "Password reset email sent! Check your inbox." });

        // Close modal after 3 seconds
        setTimeout(() => {
          this.showResetPasswordModal = false;
          this.emailsentValue = false; // Reset for next time
        }, 3000);
      } catch (error) {
        console.error("Password reset error:", error);

        // Handle specific Firebase errors
        if (error.code === 'auth/user-not-found') {
          // For security, don't reveal if email exists
          this.emailsentValue = true;
          this.$snack.success({ text: "If an account exists, a reset email has been sent." });
          setTimeout(() => {
            this.showResetPasswordModal = false;
            this.emailsentValue = false;
          }, 3000);
        } else if (error.code === 'auth/invalid-email') {
          this.$snack.danger({ text: "Please enter a valid email address." });
        } else if (error.code === 'auth/too-many-requests') {
          this.$snack.danger({ text: "Too many requests. Please try again later." });
        } else {
          this.$snack.danger({ text: "Failed to send reset email. Please try again." });
        }
      }
    },
    async login() {
      this.loadingStuff = true;

      const { email, password } = this;

      if (password.length <= 5) {
        this.fireBaseAuthValidation();
        this.loadingStuff = false;
        return;
      }

      try {
        const auth = getAuth();

        // Sign out any existing Firebase user first to ensure clean state
        if (auth.currentUser) {
          await signOut(auth);
        }

        // Try Firebase authentication first
        try {
          const userCredential = await signInWithEmailAndPassword(auth, email, password);

          // Firebase login successful - now get user data from database
          const userData = {
            email: email,
            action: "getUserByEmail",
          };

          const response = await Users.find(userData);

          if (response.data && response.data.access_block) {
            this.accessBlock = response.data.access_block;
            this.loadingStuff = false;
            return;
          }

          if (response.data && response.data.length > 0) {
            response.data[0].uid = userCredential.user.uid;

            // Sync password to MySQL (in case it was reset via Firebase)
            await this.syncPasswordToDatabase(response.data[0].id, password);

            this.loginSuccess(response.data);
          } else {
            this.$snack.danger({ text: "User account not found in system." });
            this.loadingStuff = false;
          }

        } catch (firebaseError) {
          console.error("Firebase sign-in error:", firebaseError.code);

          // If Firebase says user not found, try the old MySQL flow
          // (for users who haven't been migrated to Firebase yet)
          if (firebaseError.code === 'auth/user-not-found' ||
            firebaseError.code === 'auth/invalid-credential') {
            await this.legacyLogin(email, password, auth);
          } else if (firebaseError.code === 'auth/wrong-password') {
            this.$snack.danger({ text: "Incorrect password." });
          } else if (firebaseError.code === 'auth/too-many-requests') {
            this.$snack.danger({ text: "Too many failed attempts. Please try again later." });
          } else {
            this.handleAuthError(firebaseError);
          }
        }

      } catch (error) {
        console.error("Login error:", error.message);
        this.$snack.danger({ text: "Login failed. Please try again." });
      } finally {
        this.loadingStuff = false;
      }
    },
    async legacyLogin(email, password, auth) {
      // Fallback to MySQL validation for users not yet in Firebase
      try {
        const response = await this.$store.dispatch(AUTH_REQUEST, {
          email,
          password,
          action: "login",
        });

        if (response === "Invalid Username or Password!") {
          this.phpAuthValidation();
        } else if (response && response.access_block) {
          this.accessBlock = response.access_block;
        } else if (response && response.length > 0) {
          // User exists in MySQL, create Firebase account
          await this.registerUser(auth, email, password, response);
        }
      } catch (error) {
        console.error("Legacy login error:", error);
        this.phpAuthValidation();
      }
    },
    async syncPasswordToDatabase(userId, password) {
      try {
        const data = {
          id: userId,
          password: password,
          action: "syncPassword",
        };
        await Users.find(data);
      } catch (error) {
        // Don't block login if sync fails, just log it
        console.error("Password sync error:", error);
      }
    },
    async signIn(auth, email, password, response) {
      /*if (window.location.href.indexOf("8080") > -1) {
        if (response.length > 0) {
          this.loginSuccess(response);
        }
      } else {*/
      try {
        const userCredential = await signInWithEmailAndPassword(
          auth,
          email,
          password,
        );
        response[0].uid = userCredential.user.uid;
        if (userCredential) {
          this.loginSuccess(response);
        }
      } catch (error) {
        console.error("Sign-in error:", error.message);
        this.handleAuthError(error);
      }
      //}
    },
    async registerUser(auth, email, password, response) {
      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );
        response[0].uid = userCredential.user.uid;
        if (userCredential) {
          this.loginSuccess(response);
          this.updatePlayerProfileUid(
            response[0].id,
            response[0].uid,
            response[0].email,
          );
        }
      } catch (error) {
        console.error("Registration error:", error.message);

        if (error.code === "auth/email-already-in-use") {
          await this.signIn(auth, email, password, response); // Try signing in if email is taken
          this.updatePlayerProfileUid(
            response[0].id,
            response[0].uid,
            response[0].email,
          );
        } else if (error.code === "auth/weak-password") {
          this.fireBaseAuthValidation();
        }
      }
    },
    async updatePlayerProfileUid(id, uid, email) {
      const data = {
        id: id,
        uid: uid,
        email: email,
        action: "updatePlayerProfileUid",
      };
      await Users.find(data).catch((error) => {
        console.log(error);
      });
    },
    handleAuthError(error) {
      if (error.code === "auth/email-already-in-use") {
        this.$snack.warning({ text: "Email already in use. Try signing in." });
      } else if (error.code === "auth/wrong-password") {
        this.$snack.danger({ text: "Incorrect password." });
      } else {
        this.$snack.danger({ text: error.message });
      }
    },
    phpAuthValidation() {
      this.validation = true;
      this.validationText = "Invalid Username or Password!";
      this.loadingStuff = false;
    },
    fireBaseAuthValidation() {
      this.validation = true;
      this.validationText =
        "Your password needs to be at least 6 characters long";
      this.loadingStuff = false;
    },
    openResetPasswordModal() {
      this.showResetPasswordModal = true;
    },
    async loginSuccess(response) {
      if (response[0].id !== undefined) {
        if (response[0].uid !== null && response[0].uid !== undefined) {
          this.validation = false;
          this.loadingStuff = false;

          if (response.length > 1) {
            const stringifiedObj = JSON.stringify(response);
            localStorage.setItem("userHasMoreThanOneAccount", stringifiedObj);
          }
          // Sync Firebase custom claims (clubId + userId) before navigating
          // so any Firestore chat reads on the dashboard land with claims
          // attached. Non-blocking on failure — strict rules are not yet
          // deployed at the time this is shipped.
          await this.syncFirebaseClaims();
          this.poupulateUserData(response[0]);
          this.$router.push("/dashboard").catch(() => { });
        } else {
          this.$snack.danger({ text: "Login error: User account not properly configured." });
          this.loadingStuff = false;
        }
      } else {
        this.validation = true;
        this.loadingStuff = false;
      }
    },
    async syncFirebaseClaims() {
      try {
        const auth = getAuth();
        const fbUser = auth.currentUser;
        if (!fbUser) return;
        const idToken = await fbUser.getIdToken();
        const base = window.location.href.indexOf("8080") > -1
          ? "http://www.localhost:8888"
          : "/personal-training-BE";
        await axios.post(
          `${base}/back-end-endpoint-firebase-claims.php`,
          { action: "syncClaims", idToken }
        );
        // Force-refresh so the new claim is in the cached token before
        // the user hits any Firestore read/write.
        await fbUser.getIdToken(true);
      } catch (e) {
        console.warn("syncFirebaseClaims failed", e);
      }
    },
    passwordCheck(response) {
      if (response === "Invalid Username or Password!") {
        this.phpAuthValidation();
      }

      this.loginSuccess(response);
    },
    poupulateUserData(userData) {
      this.$store.dispatch(USER_POPULATE, { userData });
      Storage.setJSON("selectedClientId", userData.id);

      // Set the user-token that the router guard checks for
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const token = `${today}/${userData.id}`;
      localStorage.setItem("user-token", token);
    },
  },
};
</script>

<style lang="scss">
@import "../styles/auth-styles.scss";
</style>

<style lang="scss" scoped>
.login {
  display: flex;
  flex-direction: column;
  width: 300px;
}

.reset-password {
  margin-top: 0.5rem;
  display: block;

  a {
    color: $text-primary;
    text-align: right;
    display: block;
    font-size: 0.9rem;
  }
}

.access-block {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;

  h2 {
    font-size: 1.2rem;
    margin: 0;
  }

  .pay-button {
    display: block;
    padding: 12px;
    border-radius: 8px;
    background: #198754;
    color: #fff;
    font-weight: 700;
    text-decoration: none;
  }

  .muted {
    color: #6c757d;
    font-size: 0.85rem;
    margin: 0;
  }

  .link-button {
    border: none;
    background: none;
    color: #0d6efd;
    text-decoration: underline;
    cursor: pointer;
    font-size: 0.85rem;
  }
}

.install-help {
  margin-top: 1.25rem;
  font-size: 0.9rem;
  text-align: center;

  a {
    color: $text-primary;
    font-weight: 600;
    text-decoration: underline;
  }
}

.installAlert {
  position: absolute;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 110;
  margin: 0;
  border-radius: 0;

  p {
    margin-bottom: 0;
    flex: 1;
  }

  .button-wrappers {
    display: flex;
    gap: 0.5rem;

    button {
      white-space: nowrap;
    }
  }
}

@media (min-width: 1200px) {
  .login-header {
    font-size: 1.5rem;
  }
}

// Bootstrap Vue Modal fixes for Vue 3
:deep(.modal) {
  display: block !important;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  outline: 0;

  .modal-dialog {
    position: relative;
    width: auto;
    margin: 1.75rem auto;
    pointer-events: none;

    &.modal-lg {
      max-width: 800px;
    }
  }

  .modal-content {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    pointer-events: auto;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 0.5rem;
    outline: 0;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    height: 100vh;
    overflow: scroll;
  }

  .modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #dee2e6;
    border-top-left-radius: calc(0.5rem - 1px);
    border-top-right-radius: calc(0.5rem - 1px);

    .modal-title {
      margin-bottom: 0;
      line-height: 1.5;
      font-weight: 500;
      font-size: 1.25rem;
    }

    .btn-close {
      padding: 0.5rem;
      margin: -0.5rem -0.5rem -0.5rem auto;
      background: transparent;
      border: 0;
      font-size: 1.5rem;
      font-weight: 700;
      line-height: 1;
      color: #000;
      opacity: 0.5;
      cursor: pointer;

      &:hover {
        opacity: 0.75;
      }
    }
  }

  .modal-body {
    position: relative;
    flex: 1 1 auto;
    padding: 1.5rem;

    .form {
      .form__item {
        margin-bottom: 1rem;

        .form__label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: #212529;
        }

        .form__input {
          display: block;
          width: 100%;
          padding: 0.375rem 0.75rem;
          font-size: 1rem;
          font-weight: 400;
          line-height: 1.5;
          color: #212529;
          background-color: #fff;
          background-clip: padding-box;
          border: 1px solid #ced4da;
          appearance: none;
          border-radius: 0.25rem;
          transition:
            border-color 0.15s ease-in-out,
            box-shadow 0.15s ease-in-out;

          &:focus {
            color: #212529;
            background-color: #fff;
            border-color: #80bdff;
            outline: 0;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          }
        }
      }
    }

    .green-button {
      display: inline-block;
      font-weight: 400;
      line-height: 1.5;
      text-align: center;
      text-decoration: none;
      vertical-align: middle;
      cursor: pointer;
      user-select: none;
      background-color: #28a745;
      border: 1px solid #28a745;
      color: white;
      padding: 0.5rem 1.5rem;
      font-size: 1rem;
      border-radius: 0.25rem;
      transition:
        color 0.15s ease-in-out,
        background-color 0.15s ease-in-out,
        border-color 0.15s ease-in-out,
        box-shadow 0.15s ease-in-out;

      &:hover {
        color: #fff;
        background-color: #218838;
        border-color: #1e7e34;
      }

      &:focus {
        color: #fff;
        background-color: #218838;
        border-color: #1e7e34;
        box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.5);
      }
    }
  }
}

// Modal backdrop
:deep(.modal-backdrop) {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1040;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);

  &.show {
    opacity: 0.5;
  }
}

// Ensure modal is visible when shown
:deep(.modal.show) {
  display: block !important;
}

// Fix for body when modal is open
body.modal-open {
  overflow: hidden;
  padding-right: 0 !important;
}
</style>
